import { useState } from "react";
import { format } from "date-fns";
import { MessageCircle, CalendarDays, Users, Minus, Plus, ArrowRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { BRAND } from "@/lib/site-data";

/**
 * Instant WhatsApp Quote — inline compact widget for each package card.
 * Captures dates + travellers, opens WhatsApp with a pre-filled message.
 */
export default function InstantQuote({ pkg, variant = "card" }) {
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [open, setOpen] = useState(false);

  const changeAdults = (d) => setAdults((v) => Math.max(1, Math.min(10, v + d)));
  const changeChildren = (c) => setChildren((v) => Math.max(0, Math.min(8, v + c)));

  const datesLabel = () => {
    if (!dateRange?.from) return "Choose dates";
    if (!dateRange.to) return format(dateRange.from, "dd MMM");
    return `${format(dateRange.from, "dd MMM")} → ${format(dateRange.to, "dd MMM")}`;
  };

  const travellersLabel = () => {
    const parts = [`${adults} adult${adults > 1 ? "s" : ""}`];
    if (children > 0) parts.push(`${children} child${children > 1 ? "ren" : ""}`);
    return parts.join(", ");
  };

  const buildWhatsappHref = () => {
    const lines = [
      `Hi DreamFly Travels! I'd love a quote for the *${pkg.country}* package.`,
      "",
      `• Package: ${pkg.duration}${pkg.price ? ` (from ${pkg.price})` : ""}`,
      `• Travellers: ${travellersLabel()}`,
    ];
    if (dateRange?.from) {
      lines.push(`• Preferred dates: ${datesLabel()}`);
    }
    lines.push("", "Could you share an itinerary & best pricing? Thanks!");
    const text = encodeURIComponent(lines.join("\n"));
    return `https://wa.me/${BRAND.whatsappNumber}?text=${text}`;
  };

  const onSend = () => {
    window.open(buildWhatsappHref(), "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`instant-quote-trigger-${pkg.id}`}
          className={
            variant === "ghost"
              ? "inline-flex items-center gap-2 rounded-full text-[#2C7A7B] text-sm font-medium hover:text-[#0F3D3E]"
              : "inline-flex items-center gap-2 rounded-full bg-[#2C7A7B] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#7A8F6B] transition-colors"
          }
        >
          <MessageCircle size={16} />
          Instant WhatsApp Quote
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`instant-quote-panel-${pkg.id}`}
        align="start"
        sideOffset={10}
        className="w-[320px] p-0 bg-[#FAFAFA] border-[#0F3D3E]/10 rounded-2xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(15,61,62,0.25)]"
      >
        <div className="bg-[#0F3D3E] text-[#F4EDE4] px-5 py-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7A8F6B]">
            Instant quote · {pkg.country}
          </div>
          <div className="mt-1 font-display text-xl leading-tight">
            {pkg.duration}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Dates */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1.5">
              Dates
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={`instant-quote-dates-${pkg.id}`}
                  className="w-full h-11 rounded-xl border border-[#0F3D3E]/15 bg-white px-3 text-left text-sm flex items-center gap-2 hover:border-[#0F3D3E] transition-colors"
                >
                  <CalendarDays size={16} className="text-[#2C7A7B]" />
                  <span className={dateRange?.from ? "text-[#0A0A0A]" : "text-[#4B5563]"}>
                    {datesLabel()}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0 bg-white z-[60]"
                align="start"
              >
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={1}
                  disabled={(date) =>
                    date < new Date(new Date().setHours(0, 0, 0, 0))
                  }
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Travellers */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-1.5 flex items-center gap-1.5">
              <Users size={11} /> Travellers
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-white border border-[#0F3D3E]/15 rounded-xl px-3 py-2">
                <span className="text-sm text-[#0A0A0A]">Adults</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`instant-quote-adults-minus-${pkg.id}`}
                    onClick={() => changeAdults(-1)}
                    className="size-7 rounded-full bg-[#F4EDE4] text-[#0F3D3E] flex items-center justify-center hover:bg-[#0F3D3E] hover:text-[#F4EDE4] transition-colors disabled:opacity-40"
                    disabled={adults <= 1}
                  >
                    <Minus size={13} />
                  </button>
                  <span className="min-w-[20px] text-center text-sm font-medium text-[#0F3D3E]">{adults}</span>
                  <button
                    type="button"
                    data-testid={`instant-quote-adults-plus-${pkg.id}`}
                    onClick={() => changeAdults(1)}
                    className="size-7 rounded-full bg-[#F4EDE4] text-[#0F3D3E] flex items-center justify-center hover:bg-[#0F3D3E] hover:text-[#F4EDE4] transition-colors"
                  >
                    <Plus size={13} />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between bg-white border border-[#0F3D3E]/15 rounded-xl px-3 py-2">
                <span className="text-sm text-[#0A0A0A]">Children</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`instant-quote-children-minus-${pkg.id}`}
                    onClick={() => changeChildren(-1)}
                    className="size-7 rounded-full bg-[#F4EDE4] text-[#0F3D3E] flex items-center justify-center hover:bg-[#0F3D3E] hover:text-[#F4EDE4] transition-colors disabled:opacity-40"
                    disabled={children <= 0}
                  >
                    <Minus size={13} />
                  </button>
                  <span className="min-w-[20px] text-center text-sm font-medium text-[#0F3D3E]">{children}</span>
                  <button
                    type="button"
                    data-testid={`instant-quote-children-plus-${pkg.id}`}
                    onClick={() => changeChildren(1)}
                    className="size-7 rounded-full bg-[#F4EDE4] text-[#0F3D3E] flex items-center justify-center hover:bg-[#0F3D3E] hover:text-[#F4EDE4] transition-colors"
                  >
                    <Plus size={13} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onSend}
            data-testid={`instant-quote-send-${pkg.id}`}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2C7A7B] text-white px-5 py-3 text-sm font-medium hover:bg-[#0F3D3E] transition-colors shadow-[0_12px_30px_-12px_rgba(44,122,123,0.7)]"
          >
            <MessageCircle size={16} />
            Send on WhatsApp
            <ArrowRight size={15} />
          </button>
          <p className="text-[11px] text-[#4B5563] text-center leading-relaxed">
            Opens WhatsApp with your details — a curator replies usually within the hour.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
